import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { Helmet } from 'react-helmet';

import { exportToCSV, exportToPDF } from '../../utils/exportUtils';
import { supabase } from '../../lib/supabase';

const PARTNER_COLS = ['legal_name', 'brand_name', 'partner_type', 'tier', 'status', 'country', 'health_score', 'revenue_contribution', 'partnership_start_date'];
const PARTNER_HDRS = {
  legal_name: 'Legal Name', brand_name: 'Brand Name', partner_type: 'Type',
  tier: 'Tier', status: 'Status', country: 'Country',
  health_score: 'Health Score', revenue_contribution: 'Revenue ($)', partnership_start_date: 'Start Date',
};

const OPP_COLS = ['name', 'sector', 'value', 'opp_status', 'win_probability', 'expected_close'];
const OPP_HDRS = { name: 'Opportunity', sector: 'Sector', value: 'Value (OMR)', opp_status: 'Stage', win_probability: 'Win %', expected_close: 'Close Date' };

const RISK_COLS = ['title', 'category', 'severity', 'risk_status', 'owner', 'review_date'];
const RISK_HDRS = { title: 'Risk', category: 'Category', severity: 'Severity', risk_status: 'Status', owner: 'Owner', review_date: 'Review Date' };

const CAPA_COLS = ['description', 'action_type', 'priority', 'capa_status', 'owner', 'due_date'];
const CAPA_HDRS = { description: 'Description', action_type: 'Type', priority: 'Priority', capa_status: 'Status', owner: 'Owner', due_date: 'Due Date' };

const ReportCard = ({ title, icon, color, count, description, onCSV, onPDF, loading }) => (
  <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
    <div className={`h-1.5 bg-gradient-to-r ${color}`} />
    <div className="p-24">
      <div className="flex items-start justify-between mb-16">
        <div className="flex items-center gap-12">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-sm`}>
            <Icon name={icon} size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900 text-base">{title}</h3>
            <p className="text-xs text-slate-500 mt-2">{description}</p>
          </div>
        </div>
        {count !== null && (
          <span className="text-2xl font-bold text-slate-900">{loading ? '…' : count}</span>
        )}
      </div>
      <div className="flex gap-10 mt-20">
        <button
          onClick={onCSV}
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-8 px-16 py-10 bg-slate-50 border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-100 transition-colors disabled:opacity-50"
        >
          <Icon name="Download" size={14} />
          Export CSV
        </button>
        <button
          onClick={onPDF}
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-8 px-16 py-10 bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <Icon name="FileText" size={14} />
          Export PDF
        </button>
      </div>
    </div>
  </div>
);

const ReportingExportPage = () => {
  const [partners, setPartners] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [risks, setRisks] = useState([]);
  const [capas, setCapas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const [p, o, r, c] = await Promise.all([
          supabase?.from('partners')?.select('*')?.order('created_at', { ascending: false }),
          supabase?.from('opportunities')?.select('*')?.order('created_at', { ascending: false }),
          supabase?.from('risks')?.select('*')?.order('created_at', { ascending: false }),
          supabase?.from('capa_actions')?.select('*')?.order('created_at', { ascending: false }),
        ]);
        setPartners(p?.data || []);
        setOpportunities(o?.data || []);
        setRisks(r?.data || []);
        setCapas(c?.data || []);
      } catch (err) {
        console.error('Report load error:', err?.message);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  const totalRevenue = partners?.reduce((s, p) => s + Number(p?.revenue_contribution || 0), 0);
  const totalPipeline = opportunities?.reduce((s, o) => s + Number(o?.value || 0), 0);
  const openRisks = risks?.filter(r => r?.risk_status === 'open')?.length;
  const openCapas = capas?.filter(c => c?.capa_status === 'open')?.length;

  const reports = [
    {
      title: 'Partner Directory',
      icon: 'Users',
      color: 'from-primary to-secondary',
      count: partners?.length,
      description: 'All partners with tier, status, health score and revenue',
      onCSV: () => exportToCSV(partners, PARTNER_COLS, PARTNER_HDRS, 'partners_report.csv'),
      onPDF: () => exportToPDF({
        title: 'Partner Directory Report',
        subtitle: `${partners?.length} partners`,
        rows: partners,
        columns: PARTNER_COLS,
        headers: PARTNER_HDRS,
        summaryCards: [
          { label: 'Total Partners', value: partners?.length },
          { label: 'Total Revenue', value: `OMR ${(totalRevenue / 1000)?.toFixed(0)}K` },
          { label: 'Active', value: partners?.filter(p => p?.status === 'approved')?.length },
        ],
      }),
    },
    {
      title: 'Opportunities Pipeline',
      icon: 'TrendingUp',
      color: 'from-emerald-500 to-teal-500',
      count: opportunities?.length,
      description: 'Full pipeline with stage, value and win probability',
      onCSV: () => exportToCSV(opportunities, OPP_COLS, OPP_HDRS, 'opportunities_report.csv'),
      onPDF: () => exportToPDF({
        title: 'Opportunities Pipeline Report',
        subtitle: `${opportunities?.length} opportunities`,
        rows: opportunities,
        columns: OPP_COLS,
        headers: OPP_HDRS,
        summaryCards: [
          { label: 'Total Opportunities', value: opportunities?.length },
          { label: 'Pipeline Value', value: `OMR ${(totalPipeline / 1000)?.toFixed(0)}K` },
          { label: 'Won', value: opportunities?.filter(o => o?.opp_status === 'Won')?.length },
        ],
      }),
    },
    {
      title: 'Risk Register',
      icon: 'AlertTriangle',
      color: 'from-amber-500 to-orange-500',
      count: risks?.length,
      description: 'All risks with severity, status and mitigation details',
      onCSV: () => exportToCSV(risks, RISK_COLS, RISK_HDRS, 'risks_report.csv'),
      onPDF: () => exportToPDF({
        title: 'Risk Register Report',
        subtitle: `${risks?.length} risks`,
        rows: risks,
        columns: RISK_COLS,
        headers: RISK_HDRS,
        summaryCards: [
          { label: 'Total Risks', value: risks?.length },
          { label: 'Open', value: openRisks },
          { label: 'Critical', value: risks?.filter(r => r?.severity === 'critical')?.length },
        ],
      }),
    },
    {
      title: 'CAPA Actions',
      icon: 'ListChecks',
      color: 'from-violet-500 to-purple-500',
      count: capas?.length,
      description: 'Corrective and preventive actions with priority and status',
      onCSV: () => exportToCSV(capas, CAPA_COLS, CAPA_HDRS, 'capa_actions_report.csv'),
      onPDF: () => exportToPDF({
        title: 'CAPA Actions Report',
        subtitle: `${capas?.length} actions`,
        rows: capas,
        columns: CAPA_COLS,
        headers: CAPA_HDRS,
        summaryCards: [
          { label: 'Total CAPAs', value: capas?.length },
          { label: 'Open', value: openCapas },
          { label: 'Critical', value: capas?.filter(c => c?.priority === 'critical')?.length },
        ],
      }),
    },
  ];

  return (
    <>
      <Helmet><title>Reporting & Export — PartnerHub</title></Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <Header />
        <main className="pt-[148px]">
          <div className="max-w-[1400px] mx-auto px-24 py-32">
            {/* Page Header */}
            <div className="mb-32">
              <div className="flex items-center gap-12 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg">
                  <Icon name="BarChart3" size={22} className="text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 font-heading">Reporting & Export</h1>
                  <p className="text-sm text-slate-500 mt-2">Generate and download reports across all modules</p>
                </div>
              </div>
            </div>

            {/* Summary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-16 mb-32">
              {[
                { label: 'Total Partners', value: loading ? '…' : partners?.length, icon: 'Users', color: 'from-primary to-secondary' },
                { label: 'Pipeline Value', value: loading ? '…' : `OMR ${(totalPipeline / 1000)?.toFixed(0)}K`, icon: 'TrendingUp', color: 'from-emerald-500 to-teal-500' },
                { label: 'Open Risks', value: loading ? '…' : openRisks, icon: 'AlertTriangle', color: 'from-amber-500 to-orange-500' },
                { label: 'Open CAPAs', value: loading ? '…' : openCapas, icon: 'ListChecks', color: 'from-violet-500 to-purple-500' },
              ]?.map(kpi => (
                <div key={kpi?.label} className="bg-white rounded-2xl border border-slate-200/60 p-20 shadow-sm">
                  <div className="flex items-center gap-12">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${kpi?.color} flex items-center justify-center`}>
                      <Icon name={kpi?.icon} size={18} className="text-white" />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-slate-900">{kpi?.value}</div>
                      <div className="text-xs text-slate-500">{kpi?.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Report Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
              {reports?.map(r => (
                <ReportCard key={r?.title} {...r} loading={loading} />
              ))}
            </div>

            {/* Info note */}
            <div className="mt-24 flex items-start gap-12 p-20 bg-blue-50 border border-blue-100 rounded-2xl">
              <Icon name="Info" size={18} className="text-blue-500 flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm font-semibold text-blue-800">PDF Export</p>
                <p className="text-xs text-blue-600 mt-2">
                  PDF export opens a print-ready page in a new tab. Select <strong>Save as PDF</strong> in your browser's print dialog to download the file.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ReportingExportPage;
